import clsx from "clsx";
import { WarehouseElementLocation } from "components/WarehouseElements/WarehouseElementsData";
import {
  EMPLACEMENT_TYPE,
  EMPTY_DIAMOND_CLASSNAME,
  FIXE_TYPE,
  PARK_TYPE,
  TEXTE_TYPE,
} from "constants/globals";
import { TestIds } from "constants/Tests/TestIds";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import { getZoomLevel } from "store/Selectors/gridLayoutSelector";
import { selectGetElementStatus } from "store/Selectors/StatusSelectors/contentSelector";
import { getWarehousePlanDimensions } from "store/Selectors/warehousePlanSelector";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import "./scss/InteractiveGridElement.scss";

export type BorderOverrideProps = {
  removeTopBorder?: boolean;
  removeBottomBorder?: boolean;
  removeLeftBorder?: boolean;
  removeRightBorder?: boolean;
};

type Props = {
  type: string;
  id: string;
  gridElementLocationSpan: WarehouseElementLocation;
  borderOverrides?: BorderOverrideProps;
  mainGridLocation: WarehouseElementLocation;
};

export const INTERACTIVE_CLASSNAME_SUFFIX = "-interactive";
const SEARCHED_CLASSNAME = "searched";

const InteractiveGridElement = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      type,
      gridElementLocationSpan: location,
      borderOverrides,
      mainGridLocation,
    },
    ref
  ) => {
    const innerDivRef = useRef<HTMLDivElement>(null);
    const elementStatus = useAppSelector(selectGetElementStatus(id));
    const isOccupied = elementStatus.isOccupied;
    const hasStacks = elementStatus.hasStacks;
    const hasPallet = elementStatus.hasPallet;
    const hasCage = elementStatus.hasCage;
    const hasInventory = elementStatus.hasInventory;
    const isOperationInProgress = elementStatus.isOperationInProgress;
    const reservationType = elementStatus.reservationType;
    const status: string = elementStatus.status;

    const isElementSearched = useAppSelector(
      state =>
        state.gridEventSlice.searchedItemId?.toLowerCase() === id.toLowerCase()
    );

    const elementSearchedRefreshToken = useAppSelector(state => {
      if (isElementSearched)
        return state.gridEventSlice.searchedItemRefreshToken;
      else return 0;
    });

    const zoomLevel = useAppSelector(getZoomLevel);
    const originalType = type;
    const lowerCaseType = type.toLowerCase();
    const isMyWindowOpen = useAppSelector(
      state => state.gridEventSlice.openedDetailId === id
    );

    const dispatch = useAppDispatch();

    const handleCellClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      const nextId = [PARK_TYPE, EMPLACEMENT_TYPE].includes(type)
        ? isMyWindowOpen
          ? null
          : id
        : null;
      dispatch(setOpenedDetailId(nextId));
    };

    const innerDivClassName = useMemo(
      () =>
        clsx(
          "inner-div", 
          borderOverrides?.removeTopBorder && "no-top-border",
          borderOverrides?.removeBottomBorder && "no-bottom-border",
          borderOverrides?.removeLeftBorder && "no-left-border",
          borderOverrides?.removeRightBorder && "no-right-border"
        ),
      [borderOverrides]
    );

    const className = useMemo(
      () =>
        createInteractiveGridElementClassName(
          lowerCaseType,
          isOperationInProgress,
          isOccupied,
          hasStacks,
          hasPallet,
          hasCage,
          hasInventory,
          status,
          reservationType,
          borderOverrides
        ),
      [
        lowerCaseType,
        isOperationInProgress,
        isOccupied,
        hasStacks,
        hasPallet,
        hasCage,
        hasInventory,
        status,
        reservationType,
        borderOverrides,
      ]
    );

    let blinkTimer: NodeJS.Timeout;

    const handleSearch = (enCours: boolean) => {
      if (blinkTimer) {
        clearTimeout(blinkTimer);
      }
      
      if (enCours === true) {
        innerDivRef.current?.classList.add(SEARCHED_CLASSNAME);
        blinkTimer = setTimeout(() => {
          innerDivRef.current?.classList.remove(SEARCHED_CLASSNAME);
        }, 7500);
      }
      // Set a timeout to remove the class after 2000 milliseconds (2 seconds)
      // IMPORTANT: Clear the timeout if the user clicks again before the timer ends
      // or if the component unmounts (handled in useEffect below)
      return () => clearTimeout(blinkTimer);
    };
    
    useEffect(() => {
      handleSearch(isElementSearched);
    }, [elementSearchedRefreshToken]);

    const mainGridOffset: WarehouseElementLocation = {
      start: {
        x: mainGridLocation.start.x,
        y: mainGridLocation.start.y,
      },
      end: {
        x: mainGridLocation.end?.x ?? 0,
        y: mainGridLocation.end?.y ?? 0,
      },
    };

    const planStartDimensions = useAppSelector(getWarehousePlanDimensions);

    const offset =
      originalType === PARK_TYPE &&
      (location.start.x > (planStartDimensions?.start.x ?? 1) ||
        location.start.y > (planStartDimensions?.start.y ?? 1))
        ? 2
        : 1;
    const gridColumnStart = location.start.x - mainGridOffset.start.x + offset;
    const gridRowStart = location.start.y - mainGridOffset.start.y + offset;
    const gridColumnEnd = location.end
      ? `span ${Math.abs(location.end.x - location.start.x + 1)}`
      : gridColumnStart;
    const gridRowEnd = location.end
      ? `span ${Math.abs(location.end.y - location.start.y + 1)}`
      : gridRowStart;

    return (
      <div
        ref={ref}
        key={`${id}-${location.start.x}-${location.start.y}`}
        data-testid={TestIds.INTERACTIVE}
        className={`${className} ${id + INTERACTIVE_CLASSNAME_SUFFIX}`}
        style={{
          gridColumnStart,
          gridColumnEnd,
          gridRowStart,
          gridRowEnd,
        }}
        {...(lowerCaseType == EMPLACEMENT_TYPE
          ? { onClick: handleCellClick }
          : {})}>
        <div 
          ref={innerDivRef}
          className={innerDivClassName}
          >
        {(hasCage || hasPallet) && !hasStacks ? (
          <div
            data-testid={TestIds.INNER_DIAMOND}
            className={EMPTY_DIAMOND_CLASSNAME}></div>
        ) : null}
        </div>
        {createOperationRedDot(isOperationInProgress, originalType, zoomLevel)}
      </div>
    );
  }
);

InteractiveGridElement.displayName = "InteractiveGridElement";
export default InteractiveGridElement;

function createOperationRedDot(
  isOperationInProgress: boolean,
  type: string,
  zoomLevel: number
): React.ReactNode {
  if (isOperationInProgress && type === EMPLACEMENT_TYPE) {
    return (
      <div
        className="operation-marker"        
      />
    );
  }
  return null;
}

function createInteractiveGridElementClassName(
  type: string,
  isOperationInProgress: boolean,
  isOccupied: boolean,
  hasStacks: boolean,
  hasPallet: boolean,
  hasCage: boolean,
  hasInventory: boolean,
  status: string,
  reservationType: string,
  borderOverrides?: BorderOverrideProps
): string {
  const inventoryClassName = getInventoryClassName(
    isOccupied,
    hasStacks,
    hasPallet,
    hasCage,
    hasInventory
  );
  return clsx(
    "unselectable-text",
    type === PARK_TYPE || type === FIXE_TYPE || type === TEXTE_TYPE
      ? "non-interactive-element"
      : "interactive-element",
    type,
    typeof status === "string" && status !== "INVALIDE" && status,
    inventoryClassName,
    isOperationInProgress && "operation-in-progress",
    status === "RSV" && reservationType && `rsvt-${reservationType}`,
    borderOverrides?.removeTopBorder && "no-top-border",
    borderOverrides?.removeBottomBorder && "no-bottom-border",
    borderOverrides?.removeLeftBorder && "no-left-border",
    borderOverrides?.removeRightBorder && "no-right-border"
  );
}

function getInventoryClassName(
  isOccupied: boolean,
  hasStacks: boolean,
  hasPallet: boolean,
  hasCage: boolean,
  hasInventory: boolean
): string {
  let inventoryClassName = "";
  if (isOccupied) {
    inventoryClassName += "OCC ";
    if (hasInventory) {
      inventoryClassName += "inventory ";
    }
    if (hasStacks) {
      inventoryClassName += "stacks ";
    }
    if (hasCage) {
      inventoryClassName += "cage ";
    }
    if (hasPallet) {
      inventoryClassName += "pallet";
    }
    inventoryClassName = inventoryClassName.trim();
  }
  return inventoryClassName;
}
